import * as XLSX from 'xlsx';
import downloadSpreadsheet from '../downloadSpreadsheet';
import { getSubjectTitle, getLocationTitle } from '..';
import { columnType, sortTableDescending } from '../../components/DataTable/utils';
import i18n from '../../i18n';

const createHyperlink = (fileFormat, linkToSource) => {
  if (fileFormat === 'csv') {
    return `=HYPERLINK("${linkToSource}", "iiag.online")`; // eslint-disable-line i18next/no-literal-string
  }
  return 'iiag.online';
};

const parseShowAATValue = (value) => {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'undefined' || value === null) {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch (_err) {
    return value;
  }
};

const normalizeLetters = (text) =>
  text
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

const shortenedYear = (year) => {
  if (typeof year === 'undefined' || year === null) {
    return '';
  }

  const yearString = year.toString();
  return `\'${yearString.substring(yearString.length - 2)}`;
};

const deriveHeaderFromDataTable = (tableRows = []) => {
  if (!Array.isArray(tableRows) || tableRows.length === 0) {
    return {
      firstColumn: 'Measure',
      records: [],
      annualFields: {},
    };
  }

  const [firstRow] = tableRows;

  const firstColumn = firstRow?.firstColumn?.column || 'Measure';
  const records = Array.isArray(firstRow?.records) ? firstRow.records.map((record) => record.column || record.value || '') : [];

  const annualFields = {};
  if (Array.isArray(firstRow?.annualFields)) {
    firstRow.annualFields.forEach((field) => {
      if (!field?.column) {
        return;
      }
      annualFields[field.column] = field.column;
    });
  }

  return {
    firstColumn,
    records,
    annualFields,
  };
};

const createSignificantChangesHeader = (state, urlParams) => {
  const { loc = '', range1from, range1to } = urlParams;

  const selectedLocations = loc ? loc.split('-').filter(Boolean) : [];
  const firstColumn = selectedLocations.length > 1 ? 'Location' : 'Measure';

  const fromYear = typeof range1from === 'undefined' ? undefined : Number(range1from);
  const toYear = typeof range1to === 'undefined' ? undefined : Number(range1to);

  const records = [];
  if (!Number.isNaN(fromYear)) {
    records.push(fromYear);
  }
  if (!Number.isNaN(toYear)) {
    records.push(toYear);
  }

  if (records.length === 0 && Array.isArray(state.tableHeader?.records)) {
    records.push(...state.tableHeader.records);
  }

  const startForLabel = records[0];
  const endForLabel = records[records.length - 1];

  const annualFields = {
    AAT_Range_1_Rounded: startForLabel && endForLabel ? [i18n.t('Change'), `${shortenedYear(startForLabel)}-${shortenedYear(endForLabel)}`] : [i18n.t('Change')],
  };

  return {
    firstColumn,
    records,
    annualFields,
  };
};

const formatRecordValue = (value) => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }

  if (value === 'n/a') {
    return i18n.t('n/a');
  }

  const numericValue = Number.parseFloat(value);
  if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
    return numericValue.toFixed(1).toString();
  }

  return value;
};

const getSignificanceText = (significance, confidenceLevel) => {
  const conf = confidenceLevel || '95';

  // Check if there's any significant change at the specified confidence level
  const significanceValue = significance[conf];

  if (significanceValue === 1) {
    return i18n.t('Improvement is significant at {{conf}}% confidence level', { conf });
  } else if (significanceValue === -1) {
    return i18n.t('Deterioration is significant at {{conf}}% confidence level', { conf });
  } else {
    return ''; // No significant change
  }
};

export default (linkToSource, fileFormat, state, urlParams) => {
  const baseTitle = i18n.t('{{subject}} for {{location}}', {
    subject: getSubjectTitle(state),
    location: getLocationTitle(state),
  });

  const { subview, showAAT } = urlParams;
  const parsedShowAAT = parseShowAATValue(showAAT);

  const title = parsedShowAAT === 4 ? `${baseTitle} - ${i18n.t('Significant Changes')}` : baseTitle;

  // const workbook = XLSX.utils.book_new();
  const data = [[title, createHyperlink(fileFormat, linkToSource)], []];

  let tableHeader = state.tableHeader;

  if (parsedShowAAT === 4) {
    tableHeader = createSignificantChangesHeader(state, urlParams);
  }

  if (!tableHeader) {
    tableHeader = deriveHeaderFromDataTable(state.dataTable);
  }

  const visibleAnnualFields = {
    ...(tableHeader?.annualFields || {}),
  };

  if (subview === 'score') {
    if (parsedShowAAT === 1) {
      delete visibleAnnualFields.AT_Range_1;
    } else if (parsedShowAAT === 0 || parsedShowAAT === false) {
      delete visibleAnnualFields.AAT_Range_1_Rounded;
      delete visibleAnnualFields.AAT_Range_2_Rounded;
      delete visibleAnnualFields.classification;
    } else if (parsedShowAAT === 2) {
      delete visibleAnnualFields.AAT_Range_1_Rounded;
      delete visibleAnnualFields.AAT_Range_2_Rounded;
      delete visibleAnnualFields.classification;
      delete visibleAnnualFields.AT_Range_1;
    }
  }

  const header = [i18n.t(tableHeader.firstColumn || 'Measure'), ...(tableHeader.records || []), ...Object.values(visibleAnnualFields).map((annualField) => (typeof annualField === 'string' ? annualField : annualField.join(' '))), ...(parsedShowAAT === 4 ? [i18n.t('Significance')] : [])];

  let tableRows = state.dataTable || [];
  const sortColumn = urlParams.sortBy || tableHeader.firstColumn || 'Measure';
  const sortColumnType = columnType(sortColumn);
  const sortDir = urlParams.sortDir || 'des';
  tableRows = sortTableDescending({
    columnId: sortColumn,
    tableRows,
    columnType: sortColumnType,
    sortDirection: sortDir,
    measureTreeOrder: state.measureTreeOrder,
    subview,
  });
  tableRows = tableRows.map((row) => {
    const significanceText = parsedShowAAT === 4 && row.significance ? getSignificanceText(row.significance, urlParams.significantChange || '95') : '';
    return [normalizeLetters(row.firstColumn?.value || ''), ...(row.records || []).map((record) => formatRecordValue(record.value)), ...(row.annualFields || []).filter((annualField) => typeof visibleAnnualFields[annualField.column] !== 'undefined').map((annualField) => (annualField.column === 'classification' ? i18n.t(annualField.value) : annualField.value)), ...(parsedShowAAT === 4 ? [significanceText] : [])];
  });

  data.push(header);
  data.push(...tableRows);

  const ws = XLSX.utils.aoa_to_sheet(data);
  ws.B1.l = { Target: linkToSource, Tooltip: 'Find us @ SheetJS.com!' };

  if (fileFormat === 'csv') {
    const csv = XLSX.utils.sheet_to_csv(ws);
    downloadSpreadsheet(csv, 'iiag.online.csv', 'text/csv;encoding:utf-8');
  }
  // else {
  //   XLSX.utils.book_append_sheet(workbook, ws, "iiag.online");
  //   XLSX.writeFile(workbook, "iiag.online.xlsx");
  // }
};
